import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { highlightText } from "../search/mcp-chat-search";
import { McpChatSpecialistReport, SparkleIcon } from "./mcp-chat-specialist-report";
import { McpToolCallAccordion } from "./mcp-chat-tool-accordion";
import {
  getSpecialistAgentFromActivity,
  getToolActivityStatusText,
  groupToolActivitiesForTranscript,
  isSpecialistTranscriptActivity,
  sortGroupsRunningLast,
  type ToolTranscriptActivity,
  type ToolTranscriptGroup
} from "./mcp-chat-tool-transcript";
import { CheckIcon, ErrorIcon, SpinnerIcon } from "./mcp-tool-activity";

interface McpToolTranscriptProps {
  activities: ToolTranscriptActivity[];
  searchQuery?: string;
  isActiveSearchMatch?: boolean;
  mode?: "completed" | "live";
  /**
   * The backend tool running right now, when there is one. Backend tools are
   * kept off the transcript (KOA-17717 — no per-tool pills), so they have no
   * group to label this line with; passing the phrase in lets them name it
   * without creating an entry. Live mode only.
   */
  backendToolLabel?: string | null;
  showDetails?: boolean;
  /**
   * True while the chat turn is still running. Forces the wrapper open for the
   * duration of the turn so it doesn't collapse between tool runs. Inner group
   * panels no longer honor this flag — they auto-collapse as soon as their
   * own runs finish so completed groups don't stack open on screen.
   */
  chatActive?: boolean;
}

const ToolCallIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`mcp-tool-accordion__chevron${open ? " mcp-tool-accordion__chevron--open" : ""}`}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchAwareText: React.FC<{ text: string; query?: string; isActive?: boolean }> = ({ text, query, isActive }) => {
  if (!query || query.length < 2) {
    return <>{text}</>;
  }

  const segments = highlightText(text, query);
  return (
    <>
      {segments.map((segment, index) =>
        segment.isMatch ? (
          <mark
            key={index}
            className={`mcp-chat__search-highlight${isActive ? " mcp-chat__search-highlight--active" : ""}`}
          >
            {segment.text}
          </mark>
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        )
      )}
    </>
  );
};

function buildToolResultSummary(completedCount: number, errorCount: number): string {
  if (completedCount > 0 && errorCount > 0) {
    return `${completedCount} completed, ${errorCount} failed`;
  }
  if (errorCount > 0) {
    return `${errorCount} failed`;
  }
  return `${completedCount} completed`;
}

function buildLiveStatusSummary(runningCount: number, completedCount: number, errorCount: number): string {
  const parts: string[] = [];
  if (runningCount > 0) parts.push(`${runningCount} running`);
  if (completedCount > 0) parts.push(`${completedCount} completed`);
  if (errorCount > 0) parts.push(`${errorCount} failed`);
  return parts.join(", ") || "Running";
}

function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function buildGroupStatusPill(group: ToolTranscriptGroup, mode: "completed" | "live"): string {
  if (group.runningCount > 0) {
    return buildLiveStatusSummary(group.runningCount, group.completedCount, group.errorCount);
  }
  if (mode === "live") {
    return buildLiveStatusSummary(group.runningCount, group.completedCount, group.errorCount);
  }
  return buildToolResultSummary(group.completedCount, group.errorCount);
}

function buildWrapperDoneLabel(groups: ToolTranscriptGroup[]): string {
  const total = groups.length;
  const called = `${total} ${total === 1 ? "tool" : "tools"} called`;
  const errorCount = groups.reduce((sum, g) => sum + g.errorCount, 0);
  return errorCount > 0 ? `${called} · ${errorCount} failed` : called;
}

type StatusTone = "running" | "error" | "success";

function getGroupTone(group: ToolTranscriptGroup): StatusTone {
  if (group.runningCount > 0) return "running";
  if (group.errorCount > 0) return "error";
  return "success";
}

function renderStatusIcon(tone: StatusTone): React.ReactNode {
  if (tone === "running") return <SpinnerIcon />;
  if (tone === "error") return <ErrorIcon />;
  return <CheckIcon />;
}

function getStatusIconClass(tone: StatusTone): string {
  if (tone === "running") return "mcp-tool-transcript__icon--running";
  if (tone === "error") return "mcp-tool-transcript__icon--error";
  return "mcp-tool-transcript__icon--success";
}

function textMatchesQuery(text: string | undefined, query: string): boolean {
  if (!text) return false;
  return text.toLowerCase().includes(query);
}

function groupMatchesSearch(group: ToolTranscriptGroup, rawQuery: string | undefined): boolean {
  if (!rawQuery || rawQuery.length < 2) return false;
  const query = rawQuery.toLowerCase();
  if (textMatchesQuery(group.label, query)) return true;
  for (const activity of group.activities) {
    if (textMatchesQuery(activity.description, query)) return true;
    if (textMatchesQuery(getToolActivityStatusText(activity), query)) return true;
  }
  return false;
}

/**
 * Text for the wrapper header — the line directly under the waiting indicator.
 *
 * A specialist group stays "running" for its whole invocation — minutes, and up
 * to 150 LLM turns — while the tool groups beneath it start and finish in
 * seconds, so a newest-first scan almost always landed on the specialist and
 * rendered a label that could not change: "Designer…" for an entire build. The
 * header above already names the specialist, so repeating it here is pure echo.
 *
 * In LIVE mode this line answers a different question — *what is running* — and
 * never falls back to the agent name:
 *
 *   1. the running tool group whose activity started most recently;
 *   2. otherwise the backend tool, which has no group of its own because those
 *      are kept off the transcript (KOA-17717) — without this the Backend
 *      Architect's whole run showed one stale browser-tool phrase while its
 *      ~2,570 database calls went unnamed;
 *   3. otherwise the most recent tool group at all, so during the model's
 *      thinking time the line holds the last real thing that ran;
 *   4. otherwise nothing — the header above already says who is working.
 *
 * The archived transcript in the message list (`mode === "completed"`) has no
 * header above it, so it keeps the original behaviour including the specialist
 * fallback; suppressing there would leave the entry unlabelled.
 */
export function buildWrapperLabel(
  groups: ToolTranscriptGroup[],
  isDone: boolean,
  live: boolean,
  backendToolLabel?: string | null
): string {
  if (isDone) return buildWrapperDoneLabel(groups);
  if (!live) {
    const legacy: ToolTranscriptGroup | null = legacyCurrentWorkGroup(groups);
    return legacy ? `${legacy.label}…` : buildWrapperDoneLabel(groups);
  }

  const toolGroups: ToolTranscriptGroup[] = groups.filter((g) => !isSpecialistTranscriptGroup(g));
  const running: ToolTranscriptGroup | null = pickMostRecent(toolGroups.filter((g) => g.runningCount > 0));
  if (running) return `${running.label}…`;
  if (backendToolLabel != null && backendToolLabel.length > 0) return `${backendToolLabel}…`;
  const latest: ToolTranscriptGroup | null = pickMostRecent(toolGroups);
  if (latest) return `${latest.label}…`;
  return "";
}

/** Pre-split selection, kept verbatim for the archived transcript. */
function legacyCurrentWorkGroup(groups: ToolTranscriptGroup[]): ToolTranscriptGroup | null {
  if (groups.length === 0) return null;
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i].runningCount > 0) return groups[i];
  }
  return groups[groups.length - 1];
}

/**
 * Most recently started group, or null for an empty list.
 *
 * Deliberately NOT "the last element": `groups` is ordered by when each tool
 * FIRST appeared, so a tool seen early and re-run late would otherwise be
 * ranked as stale. `startTime` is stamped when an activity goes running; a
 * group with none falls back to insertion order, which is why ties resolve to
 * the later element.
 */
function pickMostRecent(groups: ToolTranscriptGroup[]): ToolTranscriptGroup | null {
  let best: ToolTranscriptGroup | null = null;
  let bestStart: number = -1;
  for (const group of groups) {
    const start: number = groupStartTime(group);
    if (start >= bestStart) {
      best = group;
      bestStart = start;
    }
  }
  return best;
}

/** Newest `startTime` among a group's activities; -1 when none carries one. */
function groupStartTime(group: ToolTranscriptGroup): number {
  let newest: number = -1;
  for (const activity of group.activities) {
    const start: number = typeof activity.startTime === "number" ? activity.startTime : -1;
    if (start > newest) newest = start;
  }
  return newest;
}

/** A group is a specialist run when its members are specialist activities. */
function isSpecialistTranscriptGroup(group: ToolTranscriptGroup): boolean {
  const first = group.activities[0];
  return first !== undefined && isSpecialistTranscriptActivity(first);
}

/**
 * Holds `value` on screen for at least `minMs` before letting the next value
 * through. When tools execute back-to-back in well under a second, the raw
 * wrapper label would otherwise strobe through each tool name faster than the
 * eye can read. Intermediate values that arrive inside the window are dropped —
 * only the latest one is applied once the window passes — so the label advances
 * at a readable cadence instead of flickering.
 */
export function useStickyLabel(value: string, minMs: number): string {
  const [display, setDisplay] = useState<string>(value);
  const lastChangeRef = useRef<number>(performance.now());
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value === display) {
      pendingRef.current = null;
      return;
    }
    const elapsed: number = performance.now() - lastChangeRef.current;
    if (elapsed >= minMs) {
      setDisplay(value);
      lastChangeRef.current = performance.now();
      pendingRef.current = null;
      return;
    }
    // Too soon since the last change: queue the latest value and arm a single
    // timer to flush it once the min window elapses. A timer already in flight
    // keeps its deadline and will pick up whatever pendingRef holds by then.
    pendingRef.current = value;
    if (timerRef.current == null) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (pendingRef.current != null) {
          setDisplay(pendingRef.current);
          lastChangeRef.current = performance.now();
          pendingRef.current = null;
        }
      }, minMs - elapsed);
    }
  }, [value, display, minMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, []);

  return display;
}

interface TranscriptListItem {
  key: string;
  text: string;
  tone?: "error" | "running";
}

/**
 * Collapse consecutive identical rows into a single row with an " ×N" suffix.
 * A group that calls the same sub-operation (e.g. `find_by_type`) dozens of
 * times used to render dozens of duplicate bullet rows; this reduces it to one
 * row per distinct (text, tone) pair.
 *
 * Buckets by (text, tone) so a mix like 40 completed + 4 failed stays split
 * into two rows keeping the error tone intact. Order follows first-seen so
 * the visible sequence still matches arrival order.
 */
function coalesceListItems(items: TranscriptListItem[]): TranscriptListItem[] {
  const order: string[] = [];
  const buckets = new Map<string, { item: TranscriptListItem; count: number }>();
  for (const item of items) {
    const bucketKey = `${item.tone ?? ""}\u0000${item.text}`;
    const existing = buckets.get(bucketKey);
    if (existing) {
      existing.count++;
    } else {
      buckets.set(bucketKey, { item, count: 1 });
      order.push(bucketKey);
    }
  }
  return order.map((bucketKey) => {
    const { item, count } = buckets.get(bucketKey)!;
    return count === 1 ? item : { ...item, text: `${item.text} ×${count}` };
  });
}

const TranscriptList: React.FC<{
  items: TranscriptListItem[];
  query?: string;
  isActive?: boolean;
}> = ({ items, query, isActive }) => (
  <div className="mcp-tool-transcript__list">
    {items.map((item) => (
      <div
        key={item.key}
        className={`mcp-tool-transcript__list-item${
          item.tone === "error"
            ? " mcp-tool-transcript__list-item--error"
            : item.tone === "running"
              ? " mcp-tool-transcript__list-item--running"
              : ""
        }`}
      >
        <span className="mcp-tool-transcript__list-bullet" />
        <span className="mcp-tool-transcript__list-copy">
          <span className="mcp-tool-transcript__list-text">
            <SearchAwareText text={item.text} query={query} isActive={isActive} />
          </span>
        </span>
      </div>
    ))}
  </div>
);

interface McpToolTranscriptGroupProps {
  group: ToolTranscriptGroup;
  mode: "completed" | "live";
  showDetails: boolean;
  searchQuery?: string;
  isActiveSearchMatch?: boolean;
}

const McpToolTranscriptGroup: React.FC<McpToolTranscriptGroupProps> = ({
  group,
  mode,
  showDetails,
  searchQuery,
  isActiveSearchMatch
}) => {
  const isRunning = group.runningCount > 0;
  const matchesSearch = groupMatchesSearch(group, searchQuery);
  const rawForceOpen = isRunning || matchesSearch;

  // Grace period before auto-collapsing a finished group. Without this,
  // back-to-back tool runs in the same group flicker the panel
  // open→closed→open as each run completes and the next one starts. The
  // delayed close lets a newly-starting run cancel the pending collapse so
  // the user sees a stable, continuously-open panel during a multi-tool
  // sequence. Cleared in the cleanup so re-mounts and quick toggles don't
  // strand a stale timer.
  const COLLAPSE_GRACE_MS = 5000;
  const [delayedForceOpen, setDelayedForceOpen] = useState(rawForceOpen);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (rawForceOpen) {
      if (collapseTimerRef.current != null) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      setDelayedForceOpen(true);
      return;
    }
    if (collapseTimerRef.current != null) return;
    collapseTimerRef.current = setTimeout(() => {
      collapseTimerRef.current = null;
      setDelayedForceOpen(false);
    }, COLLAPSE_GRACE_MS);
    return () => {
      if (collapseTimerRef.current != null) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [rawForceOpen]);

  const forceOpen = delayedForceOpen;
  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  const expanded = userOverride != null ? userOverride : forceOpen;

  // Reset manual override whenever the (delayed) force signal flips so
  // completed groups auto-collapse a few seconds after their runs finish and
  // newly-started runs auto-expand again. The user can still toggle manually
  // within the same phase — the override only clears on the running↔done edge.
  const lastForceRef = useRef(forceOpen);
  useEffect(() => {
    if (lastForceRef.current !== forceOpen) {
      setUserOverride(null);
      lastForceRef.current = forceOpen;
    }
  }, [forceOpen]);

  const toggle = useCallback(() => {
    setUserOverride((prev) => !(prev != null ? prev : forceOpen));
  }, [forceOpen]);

  const firstActivity = group.activities[0];
  const isSpecialist = firstActivity ? isSpecialistTranscriptActivity(firstActivity) : false;
  const specialistAgent = isSpecialist && firstActivity ? getSpecialistAgentFromActivity(firstActivity) : null;

  const isSingleActivityGroup = group.activities.length === 1;
  const callItems = coalesceListItems(
    group.activities.map((activity) => ({
      key: activity.toolCallId || activity.requestId,
      text: activity.description
    }))
  );
  const resultItems = coalesceListItems(
    group.activities.map((activity) => ({
      key: activity.toolCallId || activity.requestId,
      text:
        isSingleActivityGroup && activity.duration != null && activity.status !== "running"
          ? `${getToolActivityStatusText(activity)} in ${formatDuration(activity.duration)}`
          : getToolActivityStatusText(activity),
      tone:
        activity.status === "error"
          ? ("error" as const)
          : activity.status === "running"
            ? ("running" as const)
            : undefined
    }))
  );

  const tone = getGroupTone(group);
  const statusSummary = buildGroupStatusPill(group, mode);
  const statusEyebrow = mode === "live" ? "Status" : "Result";
  const hasDetails = group.activities.length > 1;
  const eyebrow = isSpecialist ? "Specialist" : "Tool";
  const countNoun = isSpecialist ? "runs" : "calls";
  const countSingular = isSpecialist ? "run" : "call";

  return (
    <div
      className={`mcp-tool-transcript__group mcp-tool-transcript__group--${expanded ? "open" : "closed"}${
        isSpecialist ? " mcp-tool-transcript__group--specialist" : ""
      }`}
      data-tone={tone}
      data-specialist-agent={specialistAgent || undefined}
    >
      <button
        type="button"
        className="mcp-tool-transcript__entry mcp-tool-transcript__entry--call mcp-tool-transcript__group-header"
        aria-expanded={expanded}
        onClick={toggle}
      >
        <span
          className={`mcp-tool-transcript__icon ${
            isSpecialist ? "mcp-tool-transcript__icon--specialist" : "mcp-tool-transcript__icon--call"
          }`}
        >
          {isSpecialist ? <SparkleIcon /> : <ToolCallIcon />}
        </span>
        <div className="mcp-tool-transcript__body">
          <div className="mcp-tool-transcript__header">
            <span className="mcp-tool-transcript__eyebrow">{eyebrow}</span>
            <span className="mcp-tool-transcript__title">
              <SearchAwareText text={group.label} query={searchQuery} isActive={isActiveSearchMatch} />
            </span>
            {group.activities.length > 1 && (
              <span className="mcp-tool-transcript__count">
                {group.activities.length} {countNoun}
              </span>
            )}
            {isSpecialist && group.activities.length === 1 && (
              <span className="mcp-tool-transcript__count">1 {countSingular}</span>
            )}
          </div>
        </div>
        <span className={`mcp-tool-transcript__pill mcp-tool-transcript__pill--${tone}`}>
          <span className={`mcp-tool-transcript__pill-icon ${getStatusIconClass(tone)}`}>{renderStatusIcon(tone)}</span>
          <span className="mcp-tool-transcript__pill-text">{statusSummary}</span>
        </span>
        <ChevronIcon open={expanded} />
      </button>

      <div
        className={`mcp-tool-transcript__group-body${expanded ? " mcp-tool-transcript__group-body--open" : ""}`}
        aria-hidden={!expanded}
      >
        <div className="mcp-tool-transcript__group-body-inner">
          {isSpecialist && specialistAgent ? (
            <div className="mcp-tool-transcript__specialist-runs">
              {(() => {
                // Filter: drop completed activities with empty content —
                // tool-call-only turns never produce a report, and rendering
                // them yields an empty pre block. Running activities always
                // pass through so the spinner stays visible.
                const renderable = group.activities.filter((activity) => {
                  if (activity.status === "running") return true;
                  const content = (activity.specialistAccumulated || "").trim();
                  return content.length > 0;
                });
                if (renderable.length === 0) {
                  // All activities were tool-only turns. Still surface the
                  // group in a minimal form so the user knows the specialist
                  // ran, just without a parseable report.
                  return (
                    <div className="mcp-tool-transcript__specialist-run mcp-tool-transcript__specialist-run--empty">
                      <span className="mcp-tool-transcript__list-text">{`${group.label} finished.`}</span>
                    </div>
                  );
                }
                return renderable.map((activity) => {
                  const streaming = activity.status === "running";
                  const content = activity.specialistAccumulated || "";
                  return (
                    <div
                      key={activity.toolCallId || activity.requestId}
                      className={`mcp-tool-transcript__specialist-run mcp-tool-transcript__specialist-run--${activity.status}`}
                    >
                      <McpChatSpecialistReport
                        agent={specialistAgent}
                        content={content}
                        isStreaming={streaming}
                        keepOpen
                        variant="inline"
                      />
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <>
              <TranscriptList items={callItems} query={searchQuery} isActive={isActiveSearchMatch} />

              <div className="mcp-tool-transcript__entry mcp-tool-transcript__entry--result">
                <span className={`mcp-tool-transcript__icon ${getStatusIconClass(tone)}`}>
                  {renderStatusIcon(tone)}
                </span>
                <div className="mcp-tool-transcript__body">
                  <div className="mcp-tool-transcript__header">
                    <span className="mcp-tool-transcript__eyebrow">{statusEyebrow}</span>
                    <span className="mcp-tool-transcript__title">
                      <SearchAwareText text={statusSummary} query={searchQuery} isActive={isActiveSearchMatch} />
                    </span>
                  </div>
                  <TranscriptList items={resultItems} query={searchQuery} isActive={isActiveSearchMatch} />
                  {showDetails && hasDetails && (
                    <McpToolCallAccordion
                      activities={group.activities}
                      label={`View ${group.activities.length} activity details`}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const McpToolTranscript: React.FC<McpToolTranscriptProps> = ({
  activities,
  searchQuery,
  isActiveSearchMatch,
  mode = "completed",
  backendToolLabel,
  showDetails = mode === "completed",
  chatActive = false
}) => {
  const successfulActivities = useMemo(
    () => activities.filter((a) => a.status !== "error" && a.status !== "blocked"),
    [activities]
  );
  const groups = useMemo(() => groupToolActivitiesForTranscript(successfulActivities), [successfulActivities]);

  const displayGroups = useMemo(() => (mode === "live" ? sortGroupsRunningLast(groups) : groups), [groups, mode]);

  const anyRunning = useMemo(() => groups.some((g) => g.runningCount > 0), [groups]);
  const anySearchMatch = useMemo(
    () => (searchQuery ? groups.some((g) => groupMatchesSearch(g, searchQuery)) : false),
    [groups, searchQuery]
  );

  // Default the activity stack to COLLAPSED — the wrapper header is the
  // one-liner summary ("Reading screen context…" / "3 tools called"), and the
  // user expands it via the chevron ("View activity"). We no longer force it
  // open while a turn streams (the old `mode === "live" && (anyRunning ||
  // chatActive)` arm), which used to drown the chat in a full timeline. A
  // search match still force-opens so the matched call is revealed.
  const forceWrapperOpen = anySearchMatch;

  const [wrapperOverride, setWrapperOverride] = useState<boolean | null>(null);
  const wrapperExpanded = wrapperOverride != null ? wrapperOverride : forceWrapperOpen;

  // Reset the wrapper override whenever the auto-expand signal flips, so users
  // who close it during one run get auto-expand back on the next run / next
  // search hit. While chat is active the user's manual toggle must stick —
  // only auto-reset on the single true→false flip that fires when the whole
  // turn ends.
  const lastForceWrapperRef = useRef(forceWrapperOpen);
  useEffect(() => {
    if (chatActive) return;
    if (lastForceWrapperRef.current !== forceWrapperOpen) {
      setWrapperOverride(null);
      lastForceWrapperRef.current = forceWrapperOpen;
    }
  }, [forceWrapperOpen, chatActive]);

  const toggleWrapper = useCallback(() => {
    setWrapperOverride((prev) => !(prev != null ? prev : forceWrapperOpen));
  }, [forceWrapperOpen]);

  // While a live turn is still streaming, treat the transcript as "working"
  // even in the brief gap between one tool finishing and the next starting.
  // Without this, `anyRunning` momentarily drops to false in that gap and the
  // header snaps to the done state ("N tools called" + green check) then back
  // to running — a strobe the user reads as broken. The group panels already
  // guard this with COLLAPSE_GRACE_MS; the wrapper header now matches.
  const turnActive: boolean = mode === "live" && chatActive;
  const isDone = !anyRunning && !turnActive;
  const aggregateErrorCount = groups.reduce((sum, g) => sum + g.errorCount, 0);
  const wrapperTone: StatusTone = anyRunning || turnActive ? "running" : aggregateErrorCount > 0 ? "error" : "success";
  const isLive: boolean = mode === "live";
  const rawWrapperLabel: string = buildWrapperLabel(groups, isDone, isLive, backendToolLabel);
  // Rate-limit label changes so a burst of sub-second tool runs advances the
  // header at a readable pace instead of flickering through each name. The done
  // label is authoritative and must show immediately once the turn ends.
  // NOTE: this hook must run before the empty-groups early return below to keep
  // the hook call order stable across renders.
  const stickyLabel: string = useStickyLabel(rawWrapperLabel, 650);
  const wrapperLabel: string = isDone ? rawWrapperLabel : stickyLabel;

  if (groups.length === 0) return null;

  return (
    <div
      className={`mcp-tool-transcript mcp-tool-transcript--wrapper mcp-tool-transcript--${wrapperExpanded ? "open" : "closed"}`}
      data-tone={wrapperTone}
    >
      <button
        type="button"
        className={`mcp-tool-transcript__wrapper-header${isDone ? " mcp-tool-transcript__wrapper-header--done" : ""}`}
        aria-expanded={wrapperExpanded}
        onClick={toggleWrapper}
      >
        <span className={`mcp-tool-transcript__icon ${getStatusIconClass(wrapperTone)}`}>
          {renderStatusIcon(wrapperTone)}
        </span>
        {wrapperLabel.length > 0 && (
          <span className="mcp-tool-transcript__wrapper-label">
            <SearchAwareText text={wrapperLabel} query={searchQuery} isActive={isActiveSearchMatch} />
          </span>
        )}
        <span className="mcp-tool-transcript__wrapper-toggle">
          {wrapperExpanded ? "Hide activity" : "View activity"}
        </span>
        <ChevronIcon open={wrapperExpanded} />
      </button>

      <div
        className={`mcp-tool-transcript__wrapper-body${
          wrapperExpanded ? " mcp-tool-transcript__wrapper-body--open" : ""
        }`}
        aria-hidden={!wrapperExpanded}
      >
        <div className="mcp-tool-transcript__wrapper-body-inner">
          {displayGroups.map((group) => (
            <McpToolTranscriptGroup
              key={group.key}
              group={group}
              mode={mode}
              showDetails={showDetails}
              searchQuery={searchQuery}
              isActiveSearchMatch={isActiveSearchMatch}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
